import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Gift, Key, PlayCircle, ShoppingCart, X, AlertCircle, Sparkles } from 'lucide-react';
import { gameApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useTelegram, showInterstitialAd } from '../../hooks';
import { showCombinedRewardedAds } from '../../hooks/useCombinedAds';
import { showMontagRewardedInterstitial } from '../../hooks/useMonetag';
import toast from 'react-hot-toast';

// Types matching backend
interface WheelSegment {
  _id?: string;
  label: string;
  rewardType: 'points' | 'ton' | 'keys' | 'bonus' | 'multiplier' | 'nothing';
  rewardValue: number;
  color: string;
  icon?: string;
  probability: number;
  order: number;
  isEnabled: boolean;
}

interface WheelConfig {
  spinDuration: number;
  spinRotations: number;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  keyRequired: boolean;
  keysPerSpin: number;
  adKeyEnabled: boolean;
  maxAdKeysPerDay: number;
}

interface KeyPackage {
  _id: string;
  name: string;
  description?: string;
  price: number;
  keysGranted: number;
  badge?: string;
}

// Royal color palette for wheel segments
const ROYAL_COLORS = [
  '#8B1538', // Deep Burgundy
  '#1E3A5F', // Royal Navy
  '#4A1942', // Royal Purple
  '#0D5C4D', // Emerald Green
  '#8B4513', // Royal Bronze
  '#2E1A47', // Deep Violet
  '#6B2D5B', // Plum
  '#1A4A4A', // Teal
];

export function LuckyWheel() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [canSpin, setCanSpin] = useState(false);
  const [spinKeys, setSpinKeys] = useState(0);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [adCooldownTime, setAdCooldownTime] = useState(0);
  const [todaySpins, setTodaySpins] = useState(0);
  const [winResult, setWinResult] = useState<{ reward: number; type: string; label: string } | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [watchingAdForKey, setWatchingAdForKey] = useState(false);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [keyPackages, setKeyPackages] = useState<KeyPackage[]>([]);
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Dynamic config from backend
  const [segments, setSegments] = useState<WheelSegment[]>([]);
  const [wheelConfig, setWheelConfig] = useState<WheelConfig>({
    spinDuration: 5000,
    spinRotations: 5,
    soundEnabled: true,
    vibrationEnabled: true,
    keyRequired: true,
    keysPerSpin: 1,
    adKeyEnabled: true,
    maxAdKeysPerDay: 20,
  });
  const [isEnabled, setIsEnabled] = useState(true);
  const [disabledMessage, setDisabledMessage] = useState('');

  const pendingResult = useRef<{ reward: number; type: string; label: string } | null>(null);
  const updateBalance = useAppStore((state) => state.updateBalance);
  const { hapticFeedback, hapticNotification } = useTelegram();

  // Fetch wheel status
  const fetchStatus = useCallback(async () => {
    console.log('[LuckyWheel] Fetching wheel status...');
    setIsLoading(true);
    try {
      const status = await gameApi.getWheelStatus();
      
      console.log('[LuckyWheel] Full API response:', JSON.stringify(status, null, 2));
      
      // Update state from backend
      setSpinKeys(status.spinKeys ?? 0);
      setCooldownTime(status.cooldownRemaining ?? 0);
      setAdCooldownTime(status.adCooldownRemaining ?? 0);
      setTodaySpins(status.spinsToday ?? 0);
      setCanSpin(status.canSpin ?? false);
      setIsEnabled(status.isEnabled ?? true);
      setDisabledMessage(status.disabledMessage ?? '');
      
      // Set segments from backend
      if (status.segments && Array.isArray(status.segments)) {
        console.log('[LuckyWheel] ✅ Setting', status.segments.length, 'segments from database');
        setSegments(status.segments);
        setLoadError(null);
      } else {
        console.error('[LuckyWheel] ❌ No segments in API response!');
        setLoadError('No wheel segments configured. Please contact support.');
      }
      
      // Update config from backend
      if (status.config) {
        setWheelConfig(status.config);
      }
    } catch (error) {
      console.error('[LuckyWheel] ❌ Failed to fetch wheel status:', error);
      setLoadError('Failed to load wheel configuration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Spin cooldown timer
  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            if (spinKeys > 0) setCanSpin(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownTime, spinKeys]);

  // Ad cooldown timer
  useEffect(() => {
    if (adCooldownTime > 0) {
      const timer = setInterval(() => {
        setAdCooldownTime((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [adCooldownTime]);

  const formatCooldown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Watch ad to get a key - now uses combined ads (AdsGram + Onclicka)
  const watchAdForKey = async () => {
    if (adCooldownTime > 0 || watchingAdForKey || !wheelConfig.adKeyEnabled) return;

    setWatchingAdForKey(true);
    try {
      // Show combined rewarded ads (AdsGram first, then Onclicka)
      toast.loading('Showing ads...', { id: 'wheel-ads' });
      const adResult = await showCombinedRewardedAds('lucky_wheel');
      toast.dismiss('wheel-ads');
      
      if (!adResult.success) {
        toast.error('📺 Please watch the ads to earn a spin key!', {
          duration: 3000,
          icon: '⚠️',
        });
        setWatchingAdForKey(false);
        return;
      }

      const result = await gameApi.watchAdForKey();
      
      if (result.success) {
        setSpinKeys(result.spinKeys || 0);
        setAdCooldownTime(60);
        if (cooldownTime <= 0) setCanSpin(true);
        if (wheelConfig.vibrationEnabled) hapticNotification('success');
        toast.success('🔑 +1 Spin Key earned!');
      } else {
        toast.error(result.error || 'Failed to get key');
        if (result.cooldownRemaining) {
          setAdCooldownTime(result.cooldownRemaining);
        }
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; cooldownRemaining?: number } } };
      toast.error(err.response?.data?.error || 'Failed to get key');
      if (err.response?.data?.cooldownRemaining) {
        setAdCooldownTime(err.response.data.cooldownRemaining);
      }
    } finally {
      setWatchingAdForKey(false);
    }
  };

  // Fetch key packages
  const fetchPackages = async () => {
    try {
      const result = await gameApi.getKeyPackages();
      setKeyPackages(result.packages);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  // Purchase key package
  const purchasePackage = async (packageId: string) => {
    if (purchasingPackage) return;
    
    setPurchasingPackage(packageId);
    try {
      const result = await gameApi.purchaseKeyPackage(packageId);
      
      if (result.success) {
        setSpinKeys(result.spinKeys || 0);
        if (result.newBalance !== undefined) {
          updateBalance(result.newBalance);
        }
        if (spinKeys === 0 && cooldownTime <= 0) setCanSpin(true);
        if (wheelConfig.vibrationEnabled) hapticNotification('success');
        toast.success(`🔑 +${result.keysGranted} Spin Keys purchased!`);
        setShowPackagesModal(false);
      } else {
        toast.error(result.error || 'Failed to purchase');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Purchase failed');
    } finally {
      setPurchasingPackage(null);
    }
  };

  // Main spin function
  const spinWheel = async () => {
    if (isSpinning || !canSpin || spinKeys <= 0 || cooldownTime > 0 || !isEnabled) return;

    if (wheelConfig.vibrationEnabled) hapticFeedback('heavy');

    try {
      // Call backend API to get the result FIRST
      const result = await gameApi.spinWheel();

      if (!result.success) {
        throw new Error(result.error || 'Spin failed');
      }

      // Update keys immediately
      if (result.keysRemaining !== undefined) {
        setSpinKeys(result.keysRemaining);
      }

      // Get the segment index from backend result
      const segmentIndex = result.segmentIndex ?? 0;
      
      console.log('[LuckyWheel] Backend returned segment index:', segmentIndex, 'for label:', result.rewardLabel);

      // Store the result to show after animation
      pendingResult.current = {
        reward: result.reward ?? 0,
        type: result.rewardType ?? 'points',
        label: result.rewardLabel ?? '',
      };

      // Set cooldown after spin (use config value)
      setCooldownTime(result.cooldownRemaining ?? 60);
      setCanSpin(false);

      // Update balance immediately if points were won
      if (result.rewardType === 'points' && result.newBalance !== undefined) {
        updateBalance(result.newBalance);
      }

      // Calculate rotation to land on the winning segment
      const numSegments = segments.length;
      const segmentAngle = 360 / numSegments;
      // Calculate target angle - we want the segment at the top (where pointer is)
      // Segments are drawn starting from top (-90 degrees), going clockwise
      const targetAngle = segmentAngle * segmentIndex + segmentAngle / 2;
      // We need to rotate so that targetAngle ends up at top (0 degrees after offset)
      const rotations = wheelConfig.spinRotations || 5;
      const baseRotation = rotation % 360;
      const totalRotation = rotation + (rotations * 360) + (360 - targetAngle) - baseRotation;
      
      setIsSpinning(true);
      setRotation(totalRotation);

    } catch (error: unknown) {
      if (wheelConfig.vibrationEnabled) hapticNotification('error');
      const err = error as { response?: { data?: { error?: string; cooldownRemaining?: number } } };
      toast.error(err.response?.data?.error || 'Failed to spin');
      if (err.response?.data?.cooldownRemaining) {
        setCooldownTime(err.response.data.cooldownRemaining);
      }
    }
  };

  // Handle spin animation complete
  const handleSpinEnd = async () => {
    console.log('[LuckyWheel] Spin animation complete');
    
    setIsSpinning(false);
    
    if (wheelConfig.vibrationEnabled) hapticNotification('success');
    
    // Show combined ads sequence for Lucky Wheel: Adsgram Interstitial → Monetag Rewarded Interstitial
    // User must watch ads before seeing their reward
    toast.loading('Watch ads to claim your reward...', { id: 'wheel-reward-ads' });
    
    let adsgramSuccess = false;
    let monetagSuccess = false;
    
    try {
      // Step 1: Show Adsgram Interstitial
      console.log('[LuckyWheel] Starting Adsgram interstitial ad...');
      try {
        adsgramSuccess = await showInterstitialAd();
        if (adsgramSuccess) {
          console.log('[LuckyWheel] Adsgram interstitial completed successfully');
        }
      } catch (e) {
        console.warn('[LuckyWheel] Adsgram interstitial failed:', e);
      }

      // Step 2: Show Monetag Rewarded Interstitial (with small delay for transition)
      await new Promise(resolve => setTimeout(resolve, 300));
      console.log('[LuckyWheel] Starting Monetag rewarded interstitial ad...');
      try {
        monetagSuccess = await showMontagRewardedInterstitial();
        if (monetagSuccess) {
          console.log('[LuckyWheel] Monetag rewarded interstitial completed successfully');
        }
      } catch (e) {
        console.warn('[LuckyWheel] Monetag failed:', e);
      }
      
      toast.dismiss('wheel-reward-ads');
      
      const adSuccess = adsgramSuccess || monetagSuccess;
      
      if (adSuccess) {
        console.log('[LuckyWheel] Ads completed. Networks:', {
          adsgram: adsgramSuccess,
          monetag: monetagSuccess,
        });
      } else {
        console.warn('[LuckyWheel] All ads failed');
      }
      
      // Show the reward result after ads (or if ads fail - graceful degradation)
      if (pendingResult.current) {
        setWinResult(pendingResult.current);
        setShowResult(true);
        pendingResult.current = null;
      }
    } catch (error) {
      console.error('[LuckyWheel] Error showing ads:', error);
      toast.dismiss('wheel-reward-ads');
      // Show reward on error
      if (pendingResult.current) {
        setWinResult(pendingResult.current);
        setShowResult(true);
        pendingResult.current = null;
      }
    }

    // Refresh status to get updated balance/keys from server
    setTimeout(() => {
      fetchStatus();
    }, 500);
  };

  // Helper function to adjust color brightness
  const adjustBrightness = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  };

  // Get icon for segment
  const getSegmentIcon = (segment: WheelSegment) => {
    // Check label for key-related rewards
    const labelLower = segment.label.toLowerCase();
    if (labelLower.includes('key')) {
      return '🔑';
    }
    
    switch (segment.rewardType) {
      case 'keys':
        return '🔑';
      case 'ton':
        return '💎';
      case 'bonus':
        return '🎁';
      case 'multiplier':
        return '✨';
      case 'nothing':
        return '🔄';
      default:
        return '🪙';
    }
  };

  // SVG Wheel Component
  const renderWheel = () => {
    const numSegments = segments.length;
    if (numSegments === 0) return null;

    const size = 300;
    const center = size / 2;
    const radius = size / 2 - 15;
    const innerRadius = 35;
    const segmentAngle = 360 / numSegments;

    // Create segment paths - equal size for all segments
    const createSegmentPath = (index: number) => {
      const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
      const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
      
      const x1 = center + radius * Math.cos(startAngle);
      const y1 = center + radius * Math.sin(startAngle);
      const x2 = center + radius * Math.cos(endAngle);
      const y2 = center + radius * Math.sin(endAngle);
      
      const ix1 = center + innerRadius * Math.cos(startAngle);
      const iy1 = center + innerRadius * Math.sin(startAngle);
      const ix2 = center + innerRadius * Math.cos(endAngle);
      const iy2 = center + innerRadius * Math.sin(endAngle);

      const largeArc = segmentAngle > 180 ? 1 : 0;

      return `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}`;
    };

    // Calculate text position for each segment
    const getTextPosition = (index: number) => {
      const midAngle = ((index + 0.5) * segmentAngle - 90) * (Math.PI / 180);
      const textRadius = (radius + innerRadius) / 2 + 10;
      return {
        x: center + textRadius * Math.cos(midAngle),
        y: center + textRadius * Math.sin(midAngle),
        rotation: (index + 0.5) * segmentAngle,
      };
    };

    return (
      <div className="relative" style={{ width: size + 40, height: size + 40 }}>
        {/* Outer decorative ring */}
        <svg 
          width={size + 40} 
          height={size + 40} 
          viewBox={`0 0 ${size + 40} ${size + 40}`}
          className="absolute inset-0"
        >
          <defs>
            <linearGradient id="outerRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFD700" />
              <stop offset="25%" stopColor="#FFA500" />
              <stop offset="50%" stopColor="#FFD700" />
              <stop offset="75%" stopColor="#FFA500" />
              <stop offset="100%" stopColor="#FFD700" />
            </linearGradient>
            <filter id="outerGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          
          {/* Outer golden ring */}
          <circle
            cx={(size + 40) / 2}
            cy={(size + 40) / 2}
            r={radius + 18}
            fill="none"
            stroke="url(#outerRingGrad)"
            strokeWidth="6"
            filter="url(#outerGlow)"
          />
          
          {/* Inner border of outer ring */}
          <circle
            cx={(size + 40) / 2}
            cy={(size + 40) / 2}
            r={radius + 12}
            fill="none"
            stroke="#B8860B"
            strokeWidth="2"
          />
          
          {/* Decorative lights around the wheel */}
          {Array.from({ length: 20 }).map((_, i) => {
            const angle = (i * 18 - 90) * (Math.PI / 180);
            const lightRadius = radius + 18;
            const x = (size + 40) / 2 + lightRadius * Math.cos(angle);
            const y = (size + 40) / 2 + lightRadius * Math.sin(angle);
            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy={y}
                  r={5}
                  fill={i % 2 === 0 ? '#FFD700' : '#FF6B35'}
                  className={isSpinning ? 'animate-pulse' : ''}
                  style={{ 
                    filter: 'drop-shadow(0 0 3px rgba(255, 215, 0, 0.8))',
                    animationDelay: `${i * 50}ms`
                  }}
                />
                <circle
                  cx={x}
                  cy={y}
                  r={2}
                  fill="#FFFFFF"
                  opacity="0.8"
                />
              </g>
            );
          })}
        </svg>

        {/* Main wheel - this rotates */}
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="absolute"
          style={{
            left: 20,
            top: 20,
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning 
              ? `transform ${wheelConfig.spinDuration / 1000}s cubic-bezier(0.17, 0.67, 0.12, 0.99)` 
              : 'none',
          }}
          onTransitionEnd={handleSpinEnd}
        >
          <defs>
            {/* Shadow filter for segments */}
            <filter id="segmentShadow" x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.3" />
            </filter>
            
            {/* Center button gradient */}
            <radialGradient id="centerButtonGrad" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#3a3a5a" />
              <stop offset="100%" stopColor="#1a1a2e" />
            </radialGradient>
            
            {/* Segment gradients - creating rich royal look */}
            {segments.map((segment, i) => {
              const baseColor = segment.color || ROYAL_COLORS[i % ROYAL_COLORS.length];
              return (
                <linearGradient 
                  key={`grad-${i}`} 
                  id={`segGrad${i}`} 
                  x1="0%" 
                  y1="0%" 
                  x2="100%" 
                  y2="100%"
                >
                  <stop offset="0%" stopColor={adjustBrightness(baseColor, 30)} />
                  <stop offset="50%" stopColor={baseColor} />
                  <stop offset="100%" stopColor={adjustBrightness(baseColor, -20)} />
                </linearGradient>
              );
            })}
          </defs>

          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius + 2}
            fill="#1a1a2e"
          />

          {/* Wheel segments */}
          <g filter="url(#segmentShadow)">
            {segments.map((_, i) => (
              <path
                key={i}
                d={createSegmentPath(i)}
                fill={`url(#segGrad${i})`}
                stroke="#FFD700"
                strokeWidth="1.5"
                strokeOpacity="0.4"
              />
            ))}
          </g>

          {/* Segment divider lines */}
          {segments.map((_, i) => {
            const angle = (i * segmentAngle - 90) * (Math.PI / 180);
            const x1 = center + innerRadius * Math.cos(angle);
            const y1 = center + innerRadius * Math.sin(angle);
            const x2 = center + radius * Math.cos(angle);
            const y2 = center + radius * Math.sin(angle);
            return (
              <line
                key={`line-${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#FFD700"
                strokeWidth="2"
                strokeOpacity="0.6"
              />
            );
          })}

          {/* Segment labels */}
          {segments.map((segment, i) => {
            const pos = getTextPosition(i);
            const icon = getSegmentIcon(segment);
            const labelRotation = pos.rotation;
            
            // Adjust text orientation so it's readable
            const textRotation = labelRotation > 90 && labelRotation < 270 
              ? labelRotation + 180 
              : labelRotation;
            
            return (
              <g key={`text-${i}`}>
                {/* Icon */}
                <text
                  x={pos.x}
                  y={pos.y - 6}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="18"
                  transform={`rotate(${textRotation}, ${pos.x}, ${pos.y})`}
                  style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' }}
                >
                  {icon}
                </text>
                {/* Label text */}
                <text
                  x={pos.x}
                  y={pos.y + 14}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#ffffff"
                  fontSize="9"
                  fontWeight="bold"
                  transform={`rotate(${textRotation}, ${pos.x}, ${pos.y})`}
                  style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.9))' }}
                >
                  {segment.label.length > 12 ? segment.label.slice(0, 12) + '..' : segment.label}
                </text>
              </g>
            );
          })}

          {/* Center decoration */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius + 3}
            fill="url(#centerButtonGrad)"
            stroke="#FFD700"
            strokeWidth="3"
          />
          <circle
            cx={center}
            cy={center}
            r={innerRadius - 5}
            fill="none"
            stroke="#FFD700"
            strokeWidth="1"
            strokeOpacity="0.5"
          />
          <text
            x={center}
            y={center}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="24"
          >
            🎰
          </text>
        </svg>

        {/* Pointer/Arrow at top - stays fixed */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-10">
          <svg width="36" height="48" viewBox="0 0 36 48">
            <defs>
              <linearGradient id="pointerGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#FFA500" />
                <stop offset="100%" stopColor="#FF8C00" />
              </linearGradient>
              <filter id="pointerShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.5" />
              </filter>
            </defs>
            {/* Pointer body */}
            <path
              d="M18 48 L4 18 L18 28 L32 18 Z"
              fill="url(#pointerGrad)"
              stroke="#B8860B"
              strokeWidth="2"
              filter="url(#pointerShadow)"
            />
            {/* Pointer gem */}
            <circle cx="18" cy="14" r="10" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
            <circle cx="18" cy="14" r="6" fill="#FFA500" />
            <circle cx="16" cy="12" r="2" fill="#FFFFFF" opacity="0.6" />
          </svg>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card p-6 text-center"
      >
        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-yellow-400" />
        <p className="text-white/60">Loading Lucky Wheel...</p>
      </motion.div>
    );
  }

  // Error loading segments
  if (loadError || segments.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card p-6 text-center"
      >
        <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
          <AlertCircle className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-xl font-bold mb-2">Wheel Configuration Error</h2>
        <p className="text-white/60 mb-4">{loadError || 'No wheel segments available.'}</p>
        <button
          onClick={fetchStatus}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium"
        >
          Retry
        </button>
      </motion.div>
    );
  }

  // Wheel is disabled
  if (!isEnabled) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="card p-6 text-center"
      >
        <div className="w-20 h-20 mx-auto mb-4 bg-gray-700/50 rounded-full flex items-center justify-center">
          <Gift className="w-10 h-10 text-gray-500" />
        </div>
        <h2 className="text-xl font-bold mb-2">Lucky Wheel Unavailable</h2>
        <p className="text-white/60">{disabledMessage || 'The Lucky Wheel is temporarily disabled.'}</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card p-4 sm:p-6 overflow-hidden"
    >
      {/* Header with Crown */}
      {/* <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Crown className="w-5 h-5 text-yellow-400" />
          <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
            Royal Lucky Wheel
          </h2>
          <Crown className="w-5 h-5 text-yellow-400" />
        </div>
        <p className="text-white/50 text-xs sm:text-sm">Spin to win amazing rewards!</p>
      </div> */}

      {/* Keys Display */}
      <div className="flex justify-center mb-4">
        <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-yellow-500/20 px-4 py-2 rounded-full border border-yellow-500/30">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <Key className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-400 font-bold text-xl">{spinKeys}</span>
          <span className="text-yellow-400/70 text-xs">Keys</span>
        </div>
      </div>

      {/* Wheel container */}
      <div className="flex items-center justify-center mb-4" style={{ minHeight: '360px' }}>
        {renderWheel()}
      </div>

      {/* Spin Button */}
      <div className="flex justify-center mb-4">
        <motion.button
          onClick={spinWheel}
          disabled={isSpinning || !canSpin || spinKeys <= 0 || cooldownTime > 0}
          className={`
            relative px-8 py-3 rounded-2xl font-bold text-base sm:text-lg overflow-hidden
            transition-all duration-200
            ${isSpinning || !canSpin || spinKeys <= 0 || cooldownTime > 0
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500 text-black hover:from-yellow-400 hover:via-orange-400 hover:to-yellow-400 shadow-lg shadow-orange-500/30'
            }
          `}
          whileHover={canSpin && !isSpinning ? { scale: 1.05 } : {}}
          whileTap={canSpin && !isSpinning ? { scale: 0.95 } : {}}
        >
          {isSpinning ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Spinning...
            </span>
          ) : cooldownTime > 0 ? (
            <span>Wait {formatCooldown(cooldownTime)}</span>
          ) : spinKeys <= 0 ? (
            <span>No Keys</span>
          ) : (
            <span className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5" />
              SPIN NOW
            </span>
          )}
        </motion.button>
      </div>

      {/* Get Keys Section */}
      <div className="space-y-2">
        {/* Watch Ad for Key */}
        {wheelConfig.adKeyEnabled && (
          <motion.button
            onClick={watchAdForKey}
            disabled={adCooldownTime > 0 || watchingAdForKey}
            className={`
              w-full py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2
              ${adCooldownTime > 0 || watchingAdForKey
                ? 'bg-gray-700 text-gray-400'
                : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500'
              }
            `}
            whileHover={adCooldownTime <= 0 && !watchingAdForKey ? { scale: 1.02 } : {}}
            whileTap={adCooldownTime <= 0 && !watchingAdForKey ? { scale: 0.98 } : {}}
          >
            {watchingAdForKey ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Watching...</span>
              </>
            ) : adCooldownTime > 0 ? (
              <>
                <PlayCircle className="w-4 h-4" />
                <span>Watch Ad ({formatCooldown(adCooldownTime)})</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4" />
                <span>🎬 Watch Ad for Free Key</span>
              </>
            )}
          </motion.button>
        )}

        {/* Buy Keys Button */}
        <motion.button
          onClick={() => {
            fetchPackages();
            setShowPackagesModal(true);
          }}
          className="w-full py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-400 hover:to-pink-400"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ShoppingCart className="w-4 h-4" />
          <span>💎 Buy More Keys</span>
        </motion.button>
      </div>

      {/* Today's Spins */}
      <div className="mt-3 text-center text-white/40 text-xs">
        Today's spins: {todaySpins}
      </div>

      {/* Win Result Modal */}
      <AnimatePresence>
        {showResult && winResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"
            onClick={() => setShowResult(false)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-3xl p-6 sm:p-8 text-center max-w-sm w-full border border-yellow-500/30 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  winResult.type === 'nothing' 
                    ? 'bg-gradient-to-br from-gray-600 to-gray-700' 
                    : 'bg-gradient-to-br from-yellow-400 via-orange-500 to-yellow-400 shadow-lg shadow-orange-500/50'
                }`}
              >
                {winResult.type === 'nothing' ? (
                  <span className="text-5xl">🔄</span>
                ) : winResult.type === 'keys' || winResult.label.toLowerCase().includes('key') ? (
                  <Key className="w-12 h-12 text-white" />
                ) : winResult.type === 'bonus' ? (
                  <span className="text-5xl">🎁</span>
                ) : (
                  <Gift className="w-12 h-12 text-white" />
                )}
              </motion.div>

              <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                {winResult.type === 'nothing' ? '🔄 Try Again!' : '🎉 Congratulations!'}
              </h2>
              
              <p className="text-white/60 mb-4 text-base">
                {winResult.type === 'nothing' 
                  ? "Better luck next time!"
                  : `You won ${winResult.label}!`
                }
              </p>

              {winResult.type !== 'nothing' && winResult.reward > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                  className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent mb-4"
                >
                  +{winResult.reward.toLocaleString()}
                  <span className="text-lg sm:text-xl ml-2">
                    {winResult.type === 'keys' || winResult.label.toLowerCase().includes('key') 
                      ? 'Keys' 
                      : winResult.type === 'ton' 
                        ? 'TON' 
                        : winResult.type === 'bonus'
                          ? 'Bonus'
                          : 'Points'}
                  </span>
                </motion.div>
              )}

              {/* Show message for bonus type with 0 reward value (like +1 Key) */}
              {winResult.type !== 'nothing' && winResult.reward === 0 && winResult.label && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                  className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent mb-4"
                >
                  {winResult.label}
                </motion.div>
              )}

              <button
                onClick={() => setShowResult(false)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500 text-black font-bold text-lg hover:from-yellow-400 hover:via-orange-400 hover:to-yellow-400 transition-all"
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Key Packages Modal - Full Screen */}
      <AnimatePresence>
        {showPackagesModal && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-0 bg-gradient-to-b from-gray-800 to-gray-900 z-50 flex flex-col"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowPackagesModal(false);
            }}
          >
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-yellow-600 to-orange-600 p-4 sticky top-0 z-10">
              <button
                onClick={() => setShowPackagesModal(false)}
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
                >
                  <Key className="w-5 h-5 text-white" />
                </motion.div>
                <div>
                  <h3 className="font-bold text-lg text-white">Spin Keys</h3>
                  <p className="text-white/70 text-xs">Get keys to spin the wheel</p>
                </div>
              </div>
            </div>

            {/* Packages List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {keyPackages.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-yellow-400" />
                    <p className="text-white/60">Loading packages...</p>
                  </div>
                </div>
              ) : (
                keyPackages.map((pkg) => {
                  const isPurchasing = purchasingPackage === pkg._id;
                  
                  return (
                    <motion.div
                      key={pkg._id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="relative p-4 rounded-xl border-2 transition-all flex items-center justify-between gap-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/50 hover:border-yellow-400 shadow-lg"
                    >
                      {/* Badge */}
                      {pkg.badge && (
                        <motion.div
                          initial={{ rotate: -45, y: -10 }}
                          animate={{ rotate: 0, y: 0 }}
                          className="absolute -top-3 -left-3 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-xs font-bold text-black shadow-lg"
                        >
                          {pkg.badge}
                        </motion.div>
                      )}

                      {/* Left Section - Icon and Info */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                          <Key className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-white text-sm">{pkg.name}</h4>
                          <p className="text-yellow-300 font-bold text-lg">+{pkg.keysGranted}</p>
                          {pkg.description && (
                            <p className="text-white/60 text-xs truncate">{pkg.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Right Section - Price and Button */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-white/60 text-xs">Cost</p>
                          <p className="font-bold text-white">{pkg.price}</p>
                        </div>
                        <motion.button
                          whileTap={{ scale: 0.92 }}
                          onClick={() => purchasePackage(pkg._id)}
                          disabled={isPurchasing}
                          className={`py-2 px-4 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-all whitespace-nowrap ${
                            isPurchasing
                              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:shadow-lg shadow-yellow-500/50'
                          }`}
                        >
                          {isPurchasing ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <ShoppingCart className="w-3.5 h-3.5" />
                              Buy
                            </>
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
